import React, { useRef, useEffect } from "react";

/**
 * MagneticButton — primary CTA wrapper that adds:
 *   - subtle magnetic translate toward the cursor on desktop
 *   - micro press on click
 *   - disabled on touch devices and when prefers-reduced-motion is set
 *
 * Wraps a real <button> so all click handlers, data-testid, type=submit etc still work.
 * The transform is written straight to the DOM inside a rAF-coalesced
 * mousemove handler; no React state, so mouse movement never re-renders
 * the tree and layout reads happen at most once per frame.
 */
const MagneticButton = React.forwardRef(function MagneticButton(
  { children, className = "", strength = 0.18, radius = 80, onClick, ...rest },
  ref
) {
  const localRef = useRef(null);
  const btnRef = ref || localRef;

  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)").matches;
    const noMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || noMotion) return undefined;
    const el = btnRef.current;
    if (!el) return undefined;

    el.style.transition = "transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)";
    el.style.willChange = "transform";

    let raf = 0;
    let lastX = 0;
    let lastY = 0;
    let current = "";

    const setTransform = (next) => {
      if (next === current) return;
      current = next;
      el.style.transform = next;
    };

    const apply = () => {
      raf = 0;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = lastX - cx;
      const dy = lastY - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const reach = radius + Math.max(rect.width, rect.height) / 2;
      if (dist < reach) {
        const factor = (1 - dist / reach) * strength;
        setTransform(`translate3d(${dx * factor}px, ${dy * factor}px, 0)`);
      } else {
        setTransform("translate3d(0,0,0)");
      }
    };

    const onMove = (e) => {
      lastX = e.clientX;
      lastY = e.clientY;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const onLeave = () => setTransform("translate3d(0,0,0)");

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
      el.style.transform = "";
      el.style.willChange = "";
    };
  }, [btnRef, radius, strength]);

  return (
    <button
      ref={btnRef}
      onClick={onClick}
      className={`btn-magnetic ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
});

export default MagneticButton;
